import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  EpaChemical,
  EpaChemicalLabel,
  EpaChemicalFilters,
  PaginatedEpaChemicalsResponse,
  EpaChemicalUpdateRequest,
  AddCustomNameRequest,
  EpaChemicalCustomName,
  EpaSyncStatus,
  TriggerSyncResponse
} from '../models/epa-chemical.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PaginatedApiResponse {
  success: boolean;
  data?: {
    chemicals: EpaChemical[];
    total: number;
    page: number;
    limit: number;
  };
  error?: string;
}

/**
 * EPA Chemicals Service (Admin)
 *
 * Service for managing EPA-synced chemicals in the admin portal.
 * Communicates with /admin/epa-chemicals endpoints.
 * EPA chemicals are read-only for EPA fields; only admin fields can be edited.
 */
@Injectable({
  providedIn: 'root'
})
export class EpaChemicalsService {
  private readonly apiUrl = `${environment.apiUrl}/admin/epa-chemicals`;

  constructor(private http: HttpClient) {}

  /**
   * Search EPA chemicals with optional filters and pagination
   *
   * @param filters - Optional filter parameters
   * @returns Observable with paginated EPA chemicals response
   */
  search(filters?: EpaChemicalFilters): Observable<PaginatedEpaChemicalsResponse> {
    const params = new URLSearchParams();

    if (filters?.page !== undefined && filters.page !== null) {
      params.append('page', filters.page.toString());
    }
    if (filters?.limit !== undefined && filters.limit !== null) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.query) {
      params.append('query', filters.query);
    }
    if (filters?.category) {
      params.append('category', filters.category);
    }
    if (filters?.signalWord) {
      params.append('signalWord', filters.signalWord);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.rupFlag !== undefined && filters.rupFlag !== null) {
      params.append('rupFlag', filters.rupFlag.toString());
    }
    if (filters?.isHidden !== undefined && filters.isHidden !== null) {
      params.append('isHidden', filters.isHidden.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl}?${queryString}` : this.apiUrl;

    return this.http.get<PaginatedApiResponse>(url).pipe(
      map((response) => {
        if (response.success && response.data) {
          return {
            chemicals: response.data.chemicals,
            total: response.data.total,
            page: response.data.page,
            pageSize: response.data.limit
          };
        }
        return { chemicals: [], total: 0, page: 1, pageSize: 20 };
      })
    );
  }

  /**
   * Get a single EPA chemical by ID with custom names and labels
   *
   * @param id - Chemical ID
   * @returns Observable with the EPA chemical or null if not found
   */
  getById(id: string): Observable<EpaChemical | null> {
    return this.http.get<ApiResponse<EpaChemical>>(`${this.apiUrl}/${id}`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return null;
      })
    );
  }

  /**
   * Update custom fields for an EPA chemical (admin fields only)
   *
   * @param id - Chemical ID
   * @param data - Custom fields to update (sdsUrl, adminNotes, isHidden)
   * @returns Observable with the updated EPA chemical
   */
  updateCustomFields(id: string, data: EpaChemicalUpdateRequest): Observable<EpaChemical> {
    return this.http.put<ApiResponse<EpaChemical>>(`${this.apiUrl}/${id}`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to update EPA chemical');
      })
    );
  }

  /**
   * Add a custom name/alias to an EPA chemical
   *
   * @param chemicalId - Chemical ID
   * @param data - Custom name data (name, nameType, isPrimary)
   * @returns Observable with the created custom name
   */
  addCustomName(chemicalId: string, data: AddCustomNameRequest): Observable<EpaChemicalCustomName> {
    return this.http.post<ApiResponse<EpaChemicalCustomName>>(
      `${this.apiUrl}/${chemicalId}/custom-names`,
      data
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to add custom name');
      })
    );
  }

  /**
   * Remove a custom name from an EPA chemical
   *
   * @param chemicalId - Chemical ID
   * @param nameId - Custom name ID to remove
   * @returns Observable that completes on success
   */
  removeCustomName(chemicalId: string, nameId: string): Observable<void> {
    return this.http.delete<ApiResponse<void> | null>(
      `${this.apiUrl}/${chemicalId}/custom-names/${nameId}`
    ).pipe(
      map((response) => {
        // Backend returns null with 204 status on success
        if (response && !response.success) {
          throw new Error(response.error || 'Failed to remove custom name');
        }
      })
    );
  }

  /**
   * Trigger a manual EPA sync job
   *
   * @returns Observable with job ID and status message
   */
  triggerSync(): Observable<TriggerSyncResponse> {
    return this.http.post<ApiResponse<TriggerSyncResponse>>(
      `${this.apiUrl}/sync`,
      {}
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to trigger sync');
      })
    );
  }

  /**
   * Trigger a PPLS-only backfill job.
   * Re-enriches chemicals from PPLS API without a full PPIS sync.
   */
  triggerPplsBackfill(): Observable<TriggerSyncResponse> {
    return this.http.post<ApiResponse<TriggerSyncResponse>>(
      `${this.apiUrl}/ppls-backfill`,
      {}
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to trigger PPLS backfill');
      })
    );
  }

  /**
   * Cancel / force-remove a stuck EPA sync job.
   * Calls DELETE /admin/epa-chemicals/sync-job
   *
   * @param jobId - Optional specific job ID to cancel
   */
  cancelSyncJob(jobId?: string): Observable<any> {
    const params = jobId ? `?jobId=${jobId}` : '';
    return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/sync-job${params}`).pipe(
      map((response) => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to cancel job');
      })
    );
  }

  /**
   * Get the current/last EPA sync job status and statistics
   *
   * @returns Observable with sync status information
   */
  getSyncStatus(): Observable<EpaSyncStatus> {
    return this.http.get<ApiResponse<EpaSyncStatus>>(
      `${this.apiUrl}/sync-status`
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        // Return default status if endpoint fails
        return {
          isRunning: false,
          progress: null,
          currentJobId: null,
          lastSyncAt: null,
          lastSyncStatus: null,
          lastSyncStats: null,
          nextScheduledSync: null
        };
      })
    );
  }

  /**
   * Sync a single EPA chemical from the PPLS API
   * Fetches enriched data (active ingredients, sites, pests, labels) and updates the record
   *
   * @param chemicalId - Chemical ID
   * @returns Observable with the updated chemical including enriched data and labels
   */
  syncFromPpls(chemicalId: string): Observable<EpaChemical & { message?: string }> {
    return this.http.post<ApiResponse<EpaChemical & { message?: string }>>(
      `${this.apiUrl}/${chemicalId}/sync-ppls`,
      {}
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to sync from PPLS API');
      })
    );
  }

  // ============================================
  // LABEL MANAGEMENT
  // ============================================

  /**
   * Add a label to an EPA chemical
   */
  addLabel(chemicalId: string, data: {
    labelUrl: string;
    labelName?: string;
    acceptedDate?: string;
    setAsCurrent?: boolean;
  }): Observable<EpaChemicalLabel> {
    return this.http.post<ApiResponse<EpaChemicalLabel>>(
      `${this.apiUrl}/${chemicalId}/labels`,
      data
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to add label');
      })
    );
  }

  /**
   * Delete a label from an EPA chemical
   */
  deleteLabel(chemicalId: string, labelId: string): Observable<void> {
    return this.http.delete<ApiResponse<void>>(
      `${this.apiUrl}/${chemicalId}/labels/${labelId}`
    ).pipe(
      map((response) => {
        if (response && !response.success) {
          throw new Error(response.error || 'Failed to delete label');
        }
      })
    );
  }

  /**
   * Toggle a label's current status
   * Returns the toggled label and all labels for the chemical
   */
  setCurrentLabel(chemicalId: string, labelId: string): Observable<{ label: EpaChemicalLabel; labels: EpaChemicalLabel[] }> {
    return this.http.put<ApiResponse<{ label: EpaChemicalLabel; labels: EpaChemicalLabel[] }>>(
      `${this.apiUrl}/${chemicalId}/labels/${labelId}/set-current`,
      {}
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to set current label');
      })
    );
  }
}
